import { shallowRef } from 'vue';
import type { EntrGroup, EntrScreen } from '@/domain/types';
import type { EntrDataTransport } from '@/transport/types';

export interface AttractGroupsObserver {
  addBreadcrumb(
    category: string,
    message: string,
    data?: Record<string, unknown>
  ): void;
  captureException(error: unknown, tag: string): void;
}

export interface UseAttractGroupsOptions {
  listGroups: EntrDataTransport['listGroups'];
  pageSize: number;
  refreshMs: number;
  isControllerRunning: () => boolean;
  getScreen: () => EntrScreen;
  isBrowseEnabled: () => boolean;
  isPollingEnabled: () => boolean;
  setBusy: (value: boolean) => void;
  showGroupsLoadError: (retry: () => void) => void;
  clearGroupsLoadError: () => void;
  onAttractReset?: () => void;
  observer: AttractGroupsObserver;
  isDocumentHidden?: () => boolean;
}

export function useAttractGroups(options: UseAttractGroupsOptions) {
  const groups = shallowRef<EntrGroup[]>([]);
  let refreshTimer: number | null = null;
  let refreshInFlight = false;
  const isDocumentHidden =
    options.isDocumentHidden ??
    (() => typeof document !== 'undefined' && document.hidden);

  async function loadGroups(
    page: number,
    loadOptions: { background?: boolean } = {}
  ): Promise<void> {
    if (refreshInFlight) return;
    const background = loadOptions.background === true;
    refreshInFlight = true;
    if (!background) {
      options.setBusy(true);
    }
    try {
      const result = await options.listGroups({
        page,
        pageSize: options.pageSize,
      });
      if (!areGroupsEqual(groups.value, result.groups)) {
        groups.value = result.groups;
      }
      if (background) {
        options.clearGroupsLoadError();
      }
    } catch (error) {
      options.observer.captureException(
        error,
        background ? 'poll_attract_groups' : 'load_groups'
      );
      options.observer.addBreadcrumb(
        'entr.groups',
        'Attract groups refresh failed',
        {
          page,
          background,
        }
      );
      if (!background) {
        options.showGroupsLoadError(() => void loadGroups(page));
      }
    } finally {
      refreshInFlight = false;
      if (!background) {
        options.setBusy(false);
      }
    }
  }

  function startPolling(): void {
    if (!options.isControllerRunning()) return;
    if (!options.isBrowseEnabled()) return;
    if (!options.isPollingEnabled()) return;
    if (options.getScreen() !== 'attract') return;
    if (isDocumentHidden()) return;
    if (refreshTimer !== null) return;

    refreshTimer = window.setTimeout(() => {
      refreshTimer = null;
      void loadGroups(1, { background: true }).finally(startPolling);
    }, options.refreshMs);
  }

  function stopPolling(): void {
    if (refreshTimer === null) return;
    window.clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  function restartPolling(restartOptions: { refreshNow?: boolean } = {}): void {
    stopPolling();
    if (!options.isBrowseEnabled()) return;
    if (!options.isPollingEnabled()) return;
    if (options.getScreen() !== 'attract') return;

    if (restartOptions.refreshNow === true) {
      void loadGroups(1, { background: true }).finally(startPolling);
      return;
    }

    startPolling();
  }

  function onEnteredAttract(): void {
    options.onAttractReset?.();
    restartPolling({ refreshNow: true });
  }

  function onVisibilityChange(): void {
    if (isDocumentHidden()) {
      stopPolling();
      return;
    }
    if (!options.isControllerRunning()) return;
    restartPolling({ refreshNow: true });
  }

  return {
    groups,
    loadGroups,
    startPolling,
    stopPolling,
    onEnteredAttract,
    onVisibilityChange,
  };
}

function areGroupsEqual(a: EntrGroup[], b: EntrGroup[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((group, index) => {
    const next = b[index];
    return (
      next !== undefined &&
      group.id === next.id &&
      group.sessionId === next.sessionId &&
      group.externalEntrId === next.externalEntrId &&
      group.lookupId === next.lookupId &&
      group.coverImageUuid === next.coverImageUuid &&
      group.coverUrl === next.coverUrl &&
      group.name === next.name &&
      group.displayName === next.displayName &&
      group.memberCount === next.memberCount &&
      group.photoState === next.photoState
    );
  });
}
